import React, { useContext, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './sideBar.css';
import { FiBarChart } from 'react-icons/fi';
import { FilePdfOutlined, FlagOutlined, LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import { Avatar, Button, Dropdown, Menu } from 'antd';
import { MyContext } from '../../context/myContext';
import { availablePdfNames } from '../../utils/constants';
import { ScreenPaths } from '../../utils/AppEnum';

const Sidebar: React.FC = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const { user, selectedProduct } = useContext(MyContext)
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [isSettingDropdownVisible, setSettingDropdownVisible] = useState(false);

    const checkFileExists = (fileName: string): boolean => {
        return availablePdfNames.includes(fileName);
    };

    const generatePdfPath = (selectedProduct: string) => {
        const pdfPath = checkFileExists(`${selectedProduct}sdlcprocess.pdf`)
            ? `/${selectedProduct}sdlcprocess.pdf`
            : "";
        return pdfPath;
    };

    const pdfPath = useMemo(() => {
        return generatePdfPath(selectedProduct);
    }, [selectedProduct])

    const handleLogOut = () => {
        sessionStorage.clear();
        if (localStorage.getItem("activeScreenPath") === ScreenPaths.UserConfig) {
            localStorage.removeItem("activeScreenPath")
        }
        navigate('/login');
    };

    const handleActiveScreen = (path: string) => {
        localStorage.setItem('activeScreenPath', path)
    }


    return (
        <div className="sidebar bg-blue-600 text-white flex flex-col justify-between">
            <ul className="sidebar-menu flex flex-col items-center pt-6">
                <li className="mb-4">
                    <img
                        src="/talentsavvy-logo-removebg.png"
                        alt="TalentSavvy Logo"
                        className="logo"
                    />
                </li>

                <li className="mb-4">
                    <Link
                        to="/performance-dashboard"
                        className={`block p-2 rounded ${location.pathname === "/performance-dashboard"
                            ? "bg-white text-blue-500"
                            : "hover:bg-blue-500"
                            }`}
                        onClick={() => handleActiveScreen('/performance-dashboard')}
                    >
                        <FiBarChart size={26} style={{ strokeWidth: 1.8, border: '1px solid', borderRadius: '4px', padding: '2px' }} />
                    </Link>
                </li>
                <li className="mb-4">
                    <Link
                        to="/release-comparison"
                        className={`block p-2 rounded ${location.pathname === "/release-comparison"
                            ? "bg-white text-blue-500"
                            : "hover:bg-blue-500"
                            }`}
                        onClick={() => handleActiveScreen('/release-comparison')}
                    >
                        <FlagOutlined style={{ fontSize: 26 }} />
                    </Link>
                </li>
                <li className="mb-4">
                    <Link
                        to="/work-item"
                        className={`block p-2 rounded ${location.pathname === "/work-item"
                            ? "bg-white text-blue-500"
                            : "hover:bg-blue-500"
                            }`}
                        onClick={() => handleActiveScreen('/work-item')}
                    >
                        <img
                            src="/lifecycle.png"
                            alt="Work Item Lifecycle"
                            className={`w-8 mx-auto transition-all duration-300 filter 
                ${location.pathname === "/work-item" ? "invert-0 brightness-0 sepia hue-rotate-180 saturate-200" : "invert brightness-0"} 
                hover:invert-0 hover:brightness-0 hover:sepia hover:hue-rotate-180 hover:saturate-200`}
                        />
                    </Link>
                </li>
                <li className="mb-4">
                    <Link
                        to="/work-in-progress"
                        className={`block p-2 rounded ${location.pathname === "/work-in-progress"
                            ? "bg-white text-blue-500"
                            : "hover:bg-blue-500"
                            }`}
                        onClick={() => handleActiveScreen('/work-in-progress')}
                    >
                        <img
                            src="/work-in-progress.png"
                            alt="Work In Progress"
                            className={`w-8 mx-auto transition-all duration-300 filter 
                ${location.pathname === "/work-in-progress" ? "invert-0 brightness-0 sepia hue-rotate-180 saturate-200" : "invert brightness-0"} 
                hover:invert-0 hover:brightness-0 hover:sepia hover:hue-rotate-180 hover:saturate-200`}
                        />
                    </Link>
                </li>
            </ul>

            <ul className="sidebar-menu flex flex-col items-center mb-6">
                {pdfPath !== '' &&
                    <li>
                        <Link
                            to={pdfPath}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`block p-3 rounded ${location.pathname === pdfPath
                                ? "bg-white text-blue-600"
                                : "hover:bg-blue-500"
                                }`}
                        >
                            <FilePdfOutlined style={{ fontSize: 26 }} />
                        </Link>
                    </li>}
                {(user?.manageUsers || user?.manageTeams) && <li>
                    <Dropdown
                        overlay={
                            <Menu style={{ minWidth: '100px' }} onClick={() => { setSettingDropdownVisible(true) }}>
                                {user?.manageUsers && <Menu.Item key="1">
                                    <Link
                                        to={ScreenPaths.UserConfig}
                                        onClick={() => handleActiveScreen(ScreenPaths.UserConfig)}
                                    >
                                        Users
                                    </Link>
                                </Menu.Item>}
                                {user?.manageTeams && <Menu.Item key="2">
                                    <Link
                                        to={ScreenPaths.TeamConfig}
                                        onClick={() => handleActiveScreen(ScreenPaths.TeamConfig)}
                                    >
                                        Teams
                                    </Link>
                                </Menu.Item>}
                                {user?.manageTeams && <Menu.Item key="3">
                                    <Link
                                        to={ScreenPaths.WorkItemStatesConfig}
                                        onClick={() => handleActiveScreen(ScreenPaths.WorkItemStatesConfig)}
                                    >
                                        Work Item States
                                    </Link>
                                </Menu.Item>}
                                {user?.manageTeams && <Menu.Item key="4">
                                    <Link
                                        to={ScreenPaths.TargetsConfig}
                                        onClick={() => handleActiveScreen(ScreenPaths.TargetsConfig)}
                                    >
                                        Performance Targets
                                    </Link>
                                </Menu.Item>}
                            </Menu>
                        }
                        open={isSettingDropdownVisible}
                        onOpenChange={setSettingDropdownVisible}
                        trigger={["hover"]}
                        placement="topRight"
                    >
                        <Button
                            type="default"
                            ghost
                            shape="circle"
                            icon={<SettingOutlined />}
                        />
                    </Dropdown>
                </li>}
                <li>
                    <Dropdown
                        overlay={
                            <Menu onClick={() => { setIsDropdownVisible(true) }}>
                                <Menu.Item key="1">
                                    {user?.email}
                                </Menu.Item>
                                <Menu.Item key="2" icon={<LogoutOutlined />} onClick={handleLogOut}>
                                    Log Out
                                </Menu.Item>
                            </Menu>
                        }
                        open={isDropdownVisible}
                        onOpenChange={setIsDropdownVisible}
                        trigger={["click"]}
                        placement="bottomRight"
                    >
                        <Button
                            type="default"
                            ghost
                            shape="circle"
                            id="accountID"
                            icon={<Avatar className='bg-white text-blue-600' size="default">
                                {user?.email?.charAt(0)?.toUpperCase()}
                            </Avatar>}
                        />
                    </Dropdown>
                </li>
            </ul >
        </div >
    );
};

export default Sidebar;
